// @ts-ignore cannot find declaration file
import { utils as dataTableUtils } from '@adc/datatable'
import type { FilterSchema } from '@adc/blueprint'

type Column = {
  apiRef?: string
  colProps?: { searchable?: boolean }
  [key: string]: unknown
}

type DSLClause = Record<string, unknown>

/**
 * Escapes OpenSearch/Lucene reserved characters. Local copy — the shared
 * `@adc/datatable` build only exports `buildDSL`, not this helper.
 * Escape `\` first so we don't double-escape our own escape sequences.
 */
const escapeOpenSearchQuery = (str: string): string => {
  if (!str) return ''
  return str.replace(/([\\+\-=&|><!(){}\[\]^"~*?:\/])/g, '\\$1')
}

/**
 * Builds a multi-word-aware text search clause for the Organizations grid.
 *
 * The shared `buildTextSearchDSL` wraps the whole string as `*acme corp*`,
 * which query_string tokenizes on whitespace — the wildcards bind only to the
 * first/last token and it parses as `*acme OR corp*`. We instead prefix-wildcard
 * each token and AND them so all words must match (query_string ORs each token
 * across the field list, ANDs across tokens), giving proper multi-word search.
 *
 * Prefix (`token*`) rather than substring (`*token*`): substring caused false
 * positives where a shorter token matched inside a longer indexed term (e.g.
 * `*lc*` matched `llc`, so "Kautzer LC" returned "Kautzer LLC"). Prefix match
 * still supports partial typing from the start of a word (`Kaut*` -> Kautzer).
 *
 * Scoped to this grid on purpose: the shared component is left untouched.
 */
export const buildOrganizationsTextSearchDSL = (
  searchText: string,
  columns: Column[]
): DSLClause | null => {
  if (!searchText || !searchText.trim()) {
    return null
  }
  const fields = columns
    .filter(column => column.colProps?.searchable === true)
    .map(column => column.apiRef)
    .filter(Boolean)

  if (fields.length === 0) {
    return null
  }

  // Mirror the standard analyzer's tokenization (UAX#29) so each client token
  // lines up with an indexed token, then prefix-wildcard each one. The analyzer
  // keeps a fixed class of connectors INSIDE a token when flanked by
  // alphanumerics — dot, apostrophe and unicode apostrophe variants — so
  // "Auction.com" and "O'Hara" stay one token while "AT&T" / hyphens split.
  // Modeling the whole class (not one symbol at a time: IGN-184 dot, IGN-199
  // apostrophe) closes the entire "special char returns 0 results" class.
  const tokens =
    searchText.match(
      /[\p{L}\p{N}]+(?:[.'\u2018\u2019\u02BC][\p{L}\p{N}]+)*/gu
    ) ?? []

  if (tokens.length === 0) {
    return null
  }

  const value = tokens
    .map(token => `${escapeOpenSearchQuery(token)}*`)
    .join(' AND ')

  return {
    query_string: {
      fields,
      value,
      default_operator: 'AND',
      // Analyze each wildcard term with the field's analyzer so mixed-case
      // input ("Abbott") matches the lowercased index, and glued separators
      // ("at&t") tokenize correctly. Keyword fields stay exact.
      analyze_wildcard: true,
    },
  }
}

/**
 * Combines filter DSL (from the shared builder, text search omitted) with the
 * grid's multi-word text search clause. Mirrors the shared builder's combine
 * shape: `{ all: {} }` when empty, the lone clause when one, else `and`.
 */
export const buildOrganizationsDSL = ({
  searchText,
  filterState,
  columns,
  filterDefinitions,
}: {
  searchText: string
  filterState: Record<string, unknown>
  columns: Column[]
  filterDefinitions: FilterSchema.FilterGroupDefinitionArray
}): DSLClause => {
  // Filter-only DSL from the shared builder (no text search — we own that).
  const filterDsl = dataTableUtils.buildDSL({
    searchText: '',
    filterState,
    columns,
    filterDefinitions,
  }) as DSLClause

  const textSearchClause = buildOrganizationsTextSearchDSL(searchText, columns)

  const expressions: DSLClause[] = []
  if (textSearchClause) {
    expressions.push(textSearchClause)
  }
  // filterDsl is `{ all: {} }` when there are no active filters.
  if (!(filterDsl as { all?: unknown }).all) {
    expressions.push(filterDsl)
  }

  if (expressions.length === 0) return { all: {} }
  if (expressions.length === 1) return expressions[0]
  return { and: { expressions } }
}
